import { API } from "aws-amplify";
import { Decision } from "@/api/model/Decision";
import { Viewpoint } from "@/api/model/Viewpoint";
import { ModelDefinition } from "@/api/model/ModelDefinition";
import { FactorResource } from "@/api/model/FactorResource";
import { ViewpointChoiceScore } from "@/store/modules/Scoring";
import { Choice } from "@/api/model/Choice";
import { ViewpointFactorResource } from "@/api/model/ViewpointFactorResource";
import { ViewpointChoiceFactor } from "@/api/model/ViewpointChoiceFactor";
import { DecisionUser } from "@/api/model/DecisionUser";
import { ChoiceList, ListType, ResourceList } from "@/api/model/ResourceList";

const API_NAME = "Viewpoint360API";

export class ViewpointApi {

    public async invokeBotComment (payload: {decisionId: string, viewpointIds: string[]}) {
        console.log("viewpointIds", payload.viewpointIds)
        let params = {
            body: {
                function_name: "invoke_bot_comment_analysis",
                decision_id: payload.decisionId,
                viewpoint_ids: JSON.stringify(payload.viewpointIds)
            }
        }
        return await API.post(API_NAME, `/new/graph/${payload.decisionId}`, params);
    }

    public async updateOwnership(oldId: string, newId: string) {
        let params = {
            body: {
                old_id: oldId,
                new_id: newId,
                env: 'Beta'
            }
        }
        console.log('updateOwnership Request Body:');
        console.log(params);
        return await API.post(API_NAME, `/new/users/upgrade`, params);
    }
    
    // API post request to generate the chart
    public async generateStackedBarChart(decisionId: string, viewpointIds?: string[], factorIds?: string[], choiceIds?: string[]){
        let viewpointsString = '[';
        viewpointIds.map((vp,index) => viewpointsString = viewpointsString.concat(`"${vp}"${index < viewpointIds.length-1?',':''}`));
        viewpointsString = viewpointsString + ']'
        let factorsString = '[';
        factorIds.map((fac, index) => factorsString = factorsString.concat(`"${fac}"${index < factorIds.length - 1 ? ',' : ''}`));
        factorsString = factorsString + ']'
        let params = {
            queryStringParameters: {
                viewpoint_ids: viewpointsString,
                factor_ids: factorsString,
                choice_ids: ""
            }
        }
        return await API.get(API_NAME, `/ds/graph/stacked_bar_chart_factor_scores/${decisionId}`, params);
    }

    // public async generateFactorImportanceChart(decisionId: string, viewpointIds?: string[], factorIds?: string[], choiceIds?: string[]) {
    //     let viewpointsString = '[';
    //     viewpointIds.map((vp, index) => viewpointsString = viewpointsString.concat(`"${vp}"${index < viewpointIds.length - 1 ? ',' : ''}`));
    //     viewpointsString = viewpointsString + ']'
    //     let factorsString = '[';
    //     factorIds.map((fac, index) => factorsString = factorsString.concat(`"${fac}"${index < factorIds.length - 1 ? ',' : ''}`));
    //     factorsString = factorsString + ']'
    //     let params = {
    //         queryStringParameters: {
    //             viewpoint_ids: viewpointsString,
    //             factor_ids: factorsString,
    //             choice_ids: ""
    //         }
    //     }
    //     return await API.get(API_NAME, `/ds/graph/factor_importance_bar_chart/${decisionId}`, params);
    // }

    public async generateViewpoint(decisionId: string, viewpointIds: string[], viewpointVariation?: string, generationFunction?: string) {
        let params = {
            body: {
                function_name: generationFunction,
                decision_id: decisionId,
                viewpoint_ids: viewpointIds,
                viewpoint_variation: viewpointVariation

            }
        }
        return await API.post(API_NAME, `/new/graph/${decisionId}`, params);
    }

    public async getApps(decisionId: string) {
        return await API.get(API_NAME, `/new/apps/${decisionId}`, {});
    }
    
    public async getApp(decisionId: string, appId:string) {
        return await API.get(API_NAME, `/new/apps/${decisionId}/${appId}`, {});
    }

    public async getAllMarketplaceItems() {
        return await API.get(API_NAME, `/new/apps/PUBLIC`, {})
    }

    public async createApp(decisionId: string, app: any) {
        let params = {
            body: app
        }
        console.log('createApp Request Body:');
        console.log(params);
        return await API.post(API_NAME, `/new/apps/${decisionId}`, params);
    }

    public async createAppTemplate(decisionId: string, app: any) {
        let params = {
            body: app
        }
        console.log('createApp Request Body:');
        console.log(params);
        return await API.post(API_NAME, `/new/apps/${decisionId}`, params);
    }

    public async createTemplate(decisionId:string, name: string, description?: string, choiceLabel?: string, viewpointLabel?: string, factorLabel?: string, pluralChoiceLabel?: string, pluralViewpointLabel?: string, pluralFactorLabel?: string, scoringSuffix?: string, scoringRangeMax?: number, templateApproved? : boolean, templateEnabled?: boolean, templatePriceMonthly?: number, stripeProductCode?: string) {
        let params = {
            body: {
                name: name,
                decisionId: "PUBLIC",
                description: description,
                choiceLabel: choiceLabel,
                viewpointLabel: viewpointLabel,
                factorLabel: factorLabel,
                pluralChoiceLabel: pluralChoiceLabel,
                pluralViewpointLabel: pluralViewpointLabel,
                pluralFactorLabel: pluralFactorLabel,
                scoringSuffix: scoringSuffix,
                scoringRangeMax: scoringRangeMax,
                templateApproved: templateApproved,
                templateEnabled: templateEnabled,
                decisionIdRef: decisionId,
                templatePriceMonthly: templatePriceMonthly,
                type: "TEMPLATE",
                stripeProductCode: stripeProductCode
            }
        }
        console.log('createApp Request Body:');
        console.log(params);
        return await API.post(API_NAME, `/new/apps/PUBLIC`, params);
    }

    public async deleteApp(decisionId: string, appId: string) {
        let params = {};
        return await API.del(API_NAME, `/new/apps/${decisionId}/${appId}`, params);
    }

    public async updateApp(decisionId: string, appId: string, app: any) {
        let params = {
            body: app
        }
        console.log('updateApps Request Body:');
        console.log(params);
        return await API.post(API_NAME, `/new/apps/${decisionId}/${appId}`, params);
    }

    public async getHistogram(decisionId: string, factorId: string) {
        return await API.get(API_NAME, `/olaf/${decisionId}/scoring-class/${factorId}`, {})
    }

    public async getUserDecisions(): Promise<Decision[]> {
        return await API.get(API_NAME, `/decisions`, {});
    }

    public async getDecision(decisionId: string): Promise<Decision> {
        return await API.get(API_NAME, `/decisions/${decisionId}`, {});
    }

    public async getDecisions(decisionIds: string[]): Promise<Decision[]> {
        let params = {};
        if (decisionIds && decisionIds.length) {
            params = {
                queryStringParameters: {
                    decisionId: decisionIds
                }
            }
        }
        return await API.get(API_NAME, `/decisions`, params);
    }

    public async createDecision(name: string, description: string, choiceLabel?: string, viewpointLabel?: string, factorLabel?: string, pluralChoiceLabel?: string, pluralViewpointLabel?: string, pluralFactorLabel?: string, scoringSuffix?: string, scoringRangeMax?: number, commentAdminTags?: string[], commentUserTags?: string[], tagsDecision?: string[], tagsDataScience?: string[], tagsMarketplace?: string[], templateApprovedKeys?: string[], stripeProductCode?: string, templateIcon?: string, templateLogo?: string, templateImageCard?: string, templateImagePage?: string, templateDescriptionCard?: string, templateDescriptionPage?: string, templateTagline?: string, decisionTemplateId?: string, templatePriceSingle?: number, templatePriceMonthly?: number, templateApproved?: boolean, templateEnabled?: boolean, publisherName?: string, fromTemplateId?: string): Promise<Decision> {
        let params = {
            body: {
                name: name,
                description: description,
                choiceLabel: choiceLabel,
                viewpointLabel: viewpointLabel,
                factorLabel: factorLabel,
                pluralChoiceLabel: pluralChoiceLabel,
                pluralViewpointLabel: pluralViewpointLabel,
                pluralFactorLabel: pluralFactorLabel,
                scoringSuffix: scoringSuffix,
                scoringRangeMax: scoringRangeMax,
                commentAdminTags: commentAdminTags,
                commentUserTags: commentUserTags,
                tagsDecision: tagsDecision,
                tagsDataScience: tagsDataScience,
                tagsMarketplace: tagsMarketplace,
                templateApprovedKeys: templateApprovedKeys,
                stripeProductCode: stripeProductCode,
                templateIcon: templateIcon,
                templateLogo: templateLogo,
                templateImageCard: templateImageCard,
                templateImagePage: templateImagePage,
                templateDescriptionCard: templateDescriptionCard,
                templateDescriptionPage: templateDescriptionPage,
                templateTagline: templateTagline,
                decisionTemplateId: decisionTemplateId,
                templatePriceSingle: templatePriceSingle,
                templatePriceMonthly: templatePriceMonthly,
                templateApproved: templateApproved,
                templateEnabled: templateEnabled,
                publisherName: publisherName,
                fromTemplateId: fromTemplateId,
            }
        }
        return await API.post(API_NAME, '/decisions', params);
    }

    public async updateDecision(decisionId: string, name?: string, description?: string, choiceLabel?: string, viewpointLabel?: string, factorLabel?: string, pluralChoiceLabel?: string, pluralViewpointLabel?: string, pluralFactorLabel?: string, group?: string, scoringSuffix?: string, scoringRangeMax?: number, commentAdminTags?: string[], commentUserTags?: string[], tagsDecision?: string[], tagsDataScience?: string[], tagsMarketplace?: string[], templateApprovedKeys?: string[], stripeProductCode?: string, templateIcon?: string, templateLogo?: string,  templateImageCard?: string,  templateImagePage?: string, templateDescriptionCard?: string, templateDescriptionPage?: string, templateTagline?: string, decisionTemplateId?: string, templatePriceSingle?: number, templatePriceMonthly?: number, templateApproved?: boolean, templateEnabled?: boolean, publisherName?: string): Promise<Decision> {
        let params = {
            body: {
                name: name,
                description: description,
                group: group,
                choiceLabel: choiceLabel,
                viewpointLabel: viewpointLabel,
                factorLabel: factorLabel,
                pluralChoiceLabel: pluralChoiceLabel,
                pluralViewpointLabel: pluralViewpointLabel,
                pluralFactorLabel: pluralFactorLabel,
                scoringSuffix: scoringSuffix,
                scoringRangeMax: scoringRangeMax,
                commentAdminTags: commentAdminTags,
                commentUserTags: commentUserTags,
                tagsDecision: tagsDecision,
                tagsDataScience: tagsDataScience,
                tagsMarketplace: tagsMarketplace,
                templateApprovedKeys: templateApprovedKeys,
                stripeProductCode: stripeProductCode,
                templateIcon: templateIcon,
                templateLogo: templateLogo,
                templateImageCard: templateImageCard,
                templateImagePage: templateImagePage,
                templateDescriptionCard: templateDescriptionCard,
                templateDescriptionPage: templateDescriptionPage,
                templateTagline: templateTagline,
                decisionTemplateId: decisionTemplateId,
                templatePriceSingle: templatePriceSingle,
                templatePriceMonthly: templatePriceMonthly,
                templateApproved: templateApproved,
                templateEnabled: templateEnabled,
                publisherName: publisherName
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}`, params);
    }

    public async deleteDecision(decisionId: string): Promise<Decision> {
        return await API.del(API_NAME, `/decisions/${decisionId}`, {});
    }

    public async getDecisionUsers(decisionId: string): Promise<DecisionUser[]> {
        return API.get(API_NAME, `/decisions/${decisionId}/users`, {});
    }

    async removeDecisionUser(decisionId: string, userId: string) {
        return await API.del(API_NAME, `/decisions/${decisionId}/users/${userId}`, {})
    }


    public async getDecisionViewpoints(decisionId: string): Promise<Viewpoint[]> {
        let params = {
            queryStringParameters: {
                decisionId: decisionId
            }
        }
        return await API.get(API_NAME, `/viewpoints`, params);
    }

    public async createViewpoint(decisionId: string, name: string, description: string, createdFromId?: string, scoringKeyDefinition?: unknown ): Promise<Viewpoint> {
        let params = {
            body: {
                decisionId: decisionId,
                name: name,
                description: description,
                scoringKeyDefinition: scoringKeyDefinition,
                createdFromId: createdFromId
            }
        }

        return await API.post(API_NAME, `/viewpoints`, params);
    }

    public async deleteViewpoint(viewpointId: string): Promise<Viewpoint> {
        return await API.del(API_NAME, `/viewpoints/${viewpointId}`, {});
    }

    public async updateViewpoint(payload: Viewpoint) {
        let params = {
            body: {
                name: payload.name,
                description: payload.description,
                visibility: payload.visibility,
                visibleUsers: payload.visibleUsers
            }
        }
        return await API.post(API_NAME, `/viewpoints/${payload.viewpointId}`, params);
    }

    public async getDecisionModel(modelId: string, revision?: string): Promise<ModelDefinition> {
        if (revision) {
            return await API.get(API_NAME, `/model/${modelId}/${revision}`, {})
        } else {
            return await API.get(API_NAME, `/model/${modelId}`, {})
        }
    }

    public async getModelFactorResources(modelId: string, revision: string | number): Promise<FactorResource[]> {
        return await API.get(API_NAME, `/model/${modelId}/${revision}/resources`, {});
    }


    public async getViewpointFactorResources(viewpointId: string) {
        return await API.get(API_NAME, `/viewpoints/${viewpointId}/factors`, {});
    }

    public async putViewpointFactorResource(viewpointId: string, viewpointFactorResource: ViewpointFactorResource) {
        let params = {
            body: viewpointFactorResource
        }

        return await API.post(API_NAME, `/viewpoints/${viewpointId}/factors/${viewpointFactorResource.id}`, params);
    }


    public async putViewpointFactorResources(viewpointId: string, viewpointFactorResources: ViewpointFactorResource[]): Promise<ViewpointFactorResource[]> {
        let params = {
            body: viewpointFactorResources
        }

        return await API.post(API_NAME, `/viewpoints/${viewpointId}/factors`, params);
    }

    public async getViewpointChoiceFactors(viewpointId: string): Promise<ViewpointChoiceFactor[]> {
        return await API.get(API_NAME, `/viewpoints/${viewpointId}/choice/factors`, {});
    }

    public async putViewpointChoiceFactors(viewpointId: string, viewpointChoiceFactors: ViewpointChoiceFactor[]): Promise<ViewpointChoiceFactor[]> {
        let params = {
            body: viewpointChoiceFactors
        }
        return await API.post(API_NAME, `/viewpoints/${viewpointId}/choice/factors`, params);
    }

    public async getScores(decisionId: string, revision: string | number, viewpointIds: string[]):
        Promise<{ viewpointScores: { [viewpointId: string]: ViewpointChoiceScore } }> {
        let params = {
            queryStringParameters: {
                viewpointId: viewpointIds
            },
        }
        return await API.get(API_NAME, `/scoring/${decisionId}/${revision}`, params);
    }

    public async getChoices(choicesSourceId: string):
        Promise<{ choices: Choice[], totalCount: number, page: number }> {
        return await API.get(API_NAME, `/choices/${choicesSourceId}`, {});
    }

    public async createChoice(choicesSourceId: string, choice: Choice) {
        let params = {
            body: [choice]
        }
        return (await API.post(API_NAME, `/choices/${choicesSourceId}`, params))[0];
    }

    public async createChoices(choicesSourceId: string, choices: Choice[]) {
        let params = {
            body: choices
        }
        return await API.post(API_NAME, `/choices/${choicesSourceId}`, params);
    }

    public async updateChoice(choicesSourceId: string, choiceId: string, choice: Choice) {
        let params = {
            body: choice
        }
        return await API.post(API_NAME, `/choices/${choicesSourceId}/${choiceId}`, params);
    }

    public async deleteChoice(choicesSourceId: string, choiceId: string) {
        return await API.del(API_NAME, `/choices/${choicesSourceId}/${choiceId}`, {});
    }

    public async ingestChoices(choicesSourceId: string, ingestParams: any) {
        let params = {
            ingestParams
        }
        return await API.post(API_NAME, `/ingest/decision/${choicesSourceId}/choices`, ingestParams);
    }

    public async convertFile(data: any, fileName: string) {
        let path = `/convert`;
        let params = {
            body: {
                data,
                fileName: fileName
            }
        }
        return await API.post(API_NAME, path, params);
    }

    public async createFactorResource(modelId: string, revisionId: number, factorResource: FactorResource): Promise<FactorResource> {
        return (await this.createFactorResources(modelId, revisionId, [factorResource]))[0];
    }

    public async generateExecutiveSummary(decisionId: string, viewpointIds: string[], reference_viewpoint_id?: string) {
        //this function doesn't need a body
        let params = {
            body: {
                function_name: "executive_summary",
                decision_id: decisionId,
                viewpoint_ids: viewpointIds,
                reference_viewpoint_id: reference_viewpoint_id

            }
        }
        let path = `/ds/insights/executive_summary/${decisionId}/?viewpoint_ids=${viewpointIds}&reference_viewpoint_id=${reference_viewpoint_id}`
        return await API.get(API_NAME, path, params);
    }

    async updateFactorResource(modelId: string, revision: number, factorResource: FactorResource) {
        let params = {
            body: factorResource
        }
        return await API.put(API_NAME, `/model/${modelId}/${revision}/resources/${factorResource.id}`, params);
    }

    async updateFactorResources(modelId: string, revision: number, factorsToBeUpdated: FactorResource[]): Promise<FactorResource[]> {
        let params = {
            body: factorsToBeUpdated
        }
        return await API.put(API_NAME, `/model/${modelId}/${revision}/resources`, params);
    }

    async createFactorResources(modelId: string, revision: number, factorResources: FactorResource[]): Promise<FactorResource[]> {
        let params = {
            body: factorResources
        }
        return await API.post(API_NAME, `/model/${modelId}/${revision}/resources`, params);
    }

    async createFactorResourceInGroup(modelId: string, revision: number, parentId: string, factorResource: FactorResource, position?: number): Promise<FactorResource[]> {
        let params = {
            body: {
                factorResource: factorResource,
                position: position
            }
        }
        return await API.post(API_NAME, `/model/${modelId}/${revision}/resources/${parentId}`, params);
    }


    async replaceModelResources(modelId: string, revision: number, factorResources: FactorResource[]) {
        let params = {
            body: factorResources
        }
        return await API.post(API_NAME, `/model/${modelId}/${revision}/resources/model`, params)
    }

    async publishModel(modelId: string) {
        return await API.post(API_NAME, `/model/${modelId}/latest/publish`, {});
    }

    async sendChoiceSubmissionEmail(decisionId: string, email: string, adminEmail: string, userDetails: string, choiceId: string, userId: string, link: string, userDetailsHtml: string, linkText: string) {
        let params = {
            body: {
                email: email,
                adminEmail: adminEmail,
                userDetails: userDetails,
                choiceId: choiceId,
                userId: userId,
                link: link,
                userDetailsHtml: userDetailsHtml,
                linkText: linkText,
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}/users`, params);
    }

    async inviteDecisionUserByEmail(decisionId: string, email: string, permissions: string) {
        let params = {
            body: {
                email: email,
                permissionAccess: permissions
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}/users`, params);
    }

    async inviteDecisionUserByUserId(decisionId: string, userId: string, permissions: string) {
        let params = {
            body: {
                userId: userId,
                permissionAccess: permissions
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}/users`, params);
    }

    async getForm(formId: string) {
        let params = {}
        return await API.post(API_NAME, `/forms/${formId}`, params);
    }

    async submitFormData(formId: string, formData: any) {
        let params = {
            body: formData
        }
        return await API.post(API_NAME, `/forms/${formId}/submit`, params);
    }

    async generateChoiceInputForm(decisionId: string, name: string, description: string, viewpointIds: any[], choiceId?: string, viewType?: string, styles?: any) {
        let params = {
            body: {
                decisionId: decisionId,
                name: name,
                description: description,
                choiceId: choiceId,
                viewpointIds: viewpointIds,
                type: 'CHOICE_INPUT',
                viewType: viewType,
                styling: styles
            }
        }
        return await API.post(API_NAME, `/forms/input/choice`, params);
    }

    async generateScoreInputForm(decisionId: string, name: string, description: string, viewpointIds: string[], choiceId: string, showBars: boolean) {
        let params = {
            body: {
                decisionId: decisionId,
                name: name,
                description: description,
                choiceId: choiceId,
                viewpointIds: viewpointIds,
                showBars: showBars,
                type: 'SCORE_INPUT'
            }
        }
        return await API.post(API_NAME, `/forms/input/score`, params);
    }

    async joinDecision(decisionId: string, passcode: string, email: string) {
        let params = {
            body: {
                decisionId: decisionId,
                passcode: passcode,
                email: email
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}/users`, params);
    }

    async joinDecisionAutocomplete(decisionId: string, userId: string, permissionAccess: string = 'EDIT') {
        let params = {
            body: {
                userId: userId,
                permissionAccess: permissionAccess,
                autocomplete: 'true',
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}/users`, params);
    }

    async modifyDecisionUserAccess(decisionId: string, userId: string, permissionAccess: string) {
        let params = {
            body: {
                permissionAccess: permissionAccess,
            }
        }
        return await API.post(API_NAME, `/decisions/${decisionId}/users/${userId}`, params);
    }


    async getDecisionLists(decisionId: string, listType?: ListType) {
        let params = {}
        if (listType) {
            params = {
                queryStringParameters: {
                    type: listType
                }
            }
        }
        return await API.get(API_NAME, `/lists/${decisionId}`, params);
    }

    async getList(decisionId: string, listId: string) {
        return await API.get(API_NAME, `/lists/${decisionId}/${listId}`, {})
    }

    async putList(decisionId: string, listId: string, list: ResourceList) {
        let params = {
            body: list
        }
        return await API.post(API_NAME, `/lists/${decisionId}/${listId}`, params);
    }

    async createLists(decisionId: string, lists: ResourceList[]) {
        let params = {
            body: lists
        }
        return await API.post(API_NAME, `/lists/${decisionId}`, params);
    }

    async deleteList(decisionId: string, listId: string) {
        return await API.del(API_NAME, `/lists/${decisionId}/${listId}`, {});
    }

}

export const VIEWPOINT_API = new ViewpointApi();